import Button from '@mui/material/Button';
import { Divider, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import ConsiderationEntry from './ConsiderationEntry';
import api from 'lib/trpcClient';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { RealisationFormState } from '../types';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { useNotification } from 'hooks/ui/useNotification';
import { groupBy, round, sumBy } from 'lodash';
import RealisationsPage from '../RealisationsPage';
import { Steps } from '../steps';
import { Column, Row } from 'further-ui/components/Layout';
import ConsiderationTotals from '../ConsiderationTotals';

type Props = {
  onPrevious: () => void;
  onNext: () => void;
  vehicle: Vehicle;
};

const EnterConsideration: React.FC<Props> = ({
  onPrevious,
  onNext,
  vehicle,
}) => {
  const { watch, getValues } =
    useFormContext<rules.CreateRealisationFormValues>();
  const notification = useNotification();

  const { state } = useFormWithStateContext<
    rules.CreateRealisationFormValues,
    RealisationFormState
  >();

  const assetId = getValues('assetId');

  const { data: asset } = api.asset.get.useQuery(
    {
      vehicleId: vehicle._id,
      assetId,
    },
    {
      enabled: !!vehicle._id && !!assetId,
    },
  );

  const validateAllocationPercentages = (
    considerations?: Array<{
      investorAllocations?: Array<{
        allocationPercentage: number;
        investmentId: string;
        holdingClassId: string;
      }>;
    }>,
  ) => {
    if (!considerations || considerations.length === 0) return true;

    return considerations.every((c) => {
      const allocations = c.investorAllocations ?? [];
      if (allocations.length === 0) return true;

      const groups = groupBy(
        allocations,
        (a) => `${a.holdingClassId}-${a.investmentId}`,
      );

      return Object.values(groups).every((group) => {
        const total = round(sumBy(group, 'allocationPercentage'), 2);
        return total === 100;
      });
    });
  };

  const considerations = watch('consideration');

  const handleNext = () => {
    if (validateAllocationPercentages(considerations)) {
      onNext();
    } else {
      notification.error(
        'Please ensure that the allocation percentages sum to 100%.',
      );
    }
  };

  const isNextStepEnabled = state.consideration.every(
    (consideration) => consideration.showAllocationsTable,
  );

  const defaultConsiderationCurrency =
    considerations[0]?.currency ?? asset?.baseCurrency ?? CurrencyCode.GBP;

  const getConsiderationCurrency = (index: number) => {
    return considerations[index].currency;
  };

  return (
    <RealisationsPage
      activeStep={Steps.EnterConsideration}
      actionBar={
        <Row spacing="sm" fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={onPrevious}>
            Previous step
          </Button>
          <Row spacing="sm">
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isNextStepEnabled}
            >
              Continue
            </Button>
          </Row>
        </Row>
      }
    >
      <Column>
        {considerations.map((consideration, index) => (
          <ConsiderationEntry
            key={consideration.considerationType}
            index={index}
            currency={getConsiderationCurrency(index)}
            selectedVehicle={vehicle}
          />
        ))}
      </Column>
      <Divider />
      <Column spacing="sm" fullWidth>
        <Typography variant="h5">Realisation summary</Typography>
        <ConsiderationTotals
          considerations={considerations}
          currency={defaultConsiderationCurrency}
        />
      </Column>
    </RealisationsPage>
  );
};

export default EnterConsideration;
